import dayjs from 'dayjs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  CodeBlock,
} from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { eventToneFor, streamOf, type AesLogEvent } from '@/api/aes';
import styles from './Aes.module.css';

interface Props {
  event: AesLogEvent | null;
  onClose: () => void;
}

/**
 * Raw event inspector — opens in a dialog on row click (mirrors the WAF event
 * log). Header carries the event name + tone; body lists the common fields and
 * the full structured payload.
 */
export function AesLogDetail({ event, onClose }: Props) {
  // Extra fields beyond the ones already surfaced in the header.
  const known: [string, unknown][] = event
    ? ([
        ['kind', event.kind],
        ['method', event.method],
        ['status', event.status],
        ['reason', event.reason],
        ['upstream_url', event.upstream_url],
      ].filter(([, v]) => v != null && v !== '') as [string, unknown][])
    : [];

  return (
    <Dialog open={!!event} onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="lg">
        {event && (
          <>
            <DialogHeader>
              <DialogTitle>
                <StatusPill tone={eventToneFor(event)}>
                  <span className={styles.eventName}>{event.event}</span>
                </StatusPill>
              </DialogTitle>
              <DialogDescription>
                {[dayjs(event.ts).format('HH:mm:ss.SSS'), event.ip, streamOf(event)]
                  .filter(Boolean)
                  .join('  ·  ')}
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className={styles.detailPanel}>
                {known.length > 0 && (
                  <dl>
                    {known.map(([k, v]) => (
                      <div key={k} className={styles.detailRow}>
                        <dt>{k}</dt>
                        <dd>{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                )}
                <CodeBlock code={JSON.stringify(event, null, 2)} wrap maxHeight={360} />
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="secondary" onClick={onClose}>Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AesLogDetail;
